import { useState, type SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  SelectField,
  Text,
  tabularNumsClass,
  type DataTableColumn,
} from "@lendcord/concord";
import { isQueryApiError } from "@/app/store/baseApi";
import { formatTimestampDate } from "@/app/dateFormat";
import { useListPeopleQuery } from "../api/people.api";
import type { PeopleRoleFilter, PeopleStatusFilter, PersonListItem } from "../people.types";
import { PeopleCounts } from "./PeopleCounts";

const PAGE_SIZE = 10;

const ROLE_FILTERS: PeopleRoleFilter[] = ["BORROWER", "LENDER"];
const STATUS_FILTERS: PeopleStatusFilter[] = ["PENDING", "IN_PROGRESS", "APPROVED", "REJECTED", "DELETED"];

const STATUS_BADGE: Record<string, "success" | "warning" | "info" | "error"> = {
  PENDING: "warning",
  IN_PROGRESS: "info",
  APPROVED: "success",
  REJECTED: "error",
  DELETED: "error",
};

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

function formatDate(iso: string) {
  return formatTimestampDate(iso);
}

function FilterIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}

function EyeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

interface PeopleListProps {
  onView: (person: PersonListItem) => void;
}

export function PeopleList({ onView }: PeopleListProps) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [roleFilter, setRoleFilter] = useState<PeopleRoleFilter | "">("");
  const [statusFilter, setStatusFilter] = useState<PeopleStatusFilter | "">("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data, isFetching, error } = useListPeopleQuery({
    role: roleFilter || undefined,
    status: statusFilter || undefined,
    page,
    size: PAGE_SIZE,
  });

  const loadError = error
    ? isQueryApiError(error)
      ? error.message
      : intl.formatMessage({ id: "lender.people.loadError" })
    : undefined;

  const hasActiveFilters = roleFilter !== "" || statusFilter !== "";
  const activeFilterCount = (roleFilter !== "" ? 1 : 0) + (statusFilter !== "" ? 1 : 0);

  function handleClearFilters() {
    setRoleFilter("");
    setStatusFilter("");
    setPage(0);
  }

  // Client-side search on top of the current server-paginated page — matches
  // members.api.ts's own precedent (search narrows what's already loaded,
  // it isn't a separate server round trip).
  const rows = data
    ? data.content.filter((person) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          person.fullName?.toLowerCase().includes(q) ||
          person.readableId?.toLowerCase().includes(q) ||
          person.centerName?.toLowerCase().includes(q) ||
          person.area?.toLowerCase().includes(q)
        );
      })
    : [];

  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const rangeStart = totalElements === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalElements);

  const columns: DataTableColumn<PersonListItem>[] = [
    {
      id: "readableId",
      header: intl.formatMessage({ id: "lender.people.columnId" }),
      cell: (p) => <span className={`font-medium ${tabularNumsClass}`}>{p.readableId}</span>,
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "fullName",
      header: intl.formatMessage({ id: "lender.people.columnName" }),
      cell: (p) => p.fullName,
      cellClassName: "whitespace-nowrap font-medium text-text",
    },
    {
      id: "applicationType",
      header: intl.formatMessage({ id: "lender.people.columnRole" }),
      cell: (p) => (
        <FormattedMessage
          id={p.applicationType === "BORROWER" ? "lender.onboarding.applicationTypeBorrower" : "lender.onboarding.applicationTypeLender"}
        />
      ),
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "loanAmount",
      header: intl.formatMessage({ id: "lender.people.columnLoanAmount" }),
      cell: (p) => (p.loanAmount != null ? currencyFormatter.format(p.loanAmount) : "–"),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "status",
      header: intl.formatMessage({ id: "lender.people.columnStatus" }),
      cell: (p) => (
        <Badge status={STATUS_BADGE[p.status] ?? "info"}>
          <FormattedMessage id={`lender.onboarding.status${p.status}`} />
        </Badge>
      ),
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "createdAt",
      header: intl.formatMessage({ id: "lender.people.columnCreatedAt" }),
      cell: (p) => formatDate(p.createdAt),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "action",
      header: intl.formatMessage({ id: "lender.people.columnAction" }),
      cell: (p) => (
        <button
          type="button"
          onClick={() => onView(p)}
          aria-label={intl.formatMessage({ id: "lender.people.viewAction" })}
          className="inline-flex items-center justify-center text-text-muted hover:text-accent-600"
        >
          <EyeIcon className="h-4 w-4" />
        </button>
      ),
      cellClassName: "whitespace-nowrap",
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <PeopleCounts />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="max-w-sm flex-1">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery("")}
            placeholder={intl.formatMessage({ id: "lender.people.searchPlaceholder" })}
          />
        </div>

        <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="relative inline-flex items-center gap-2 rounded-sm border border-border bg-surface-raised px-3 py-2 text-sm font-medium text-text hover:bg-surface"
            >
              <FilterIcon className="h-4 w-4" />
              <FormattedMessage id="lender.people.filtersButton" />
              {activeFilterCount > 0 && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-600 text-[10px] font-semibold text-on-accent">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex flex-col gap-4">
            <SelectField
              label={intl.formatMessage({ id: "lender.people.columnRole" })}
              placeholder={intl.formatMessage({ id: "lender.people.filterAllRoles" })}
              value={roleFilter}
              onValueChange={(value) => {
                setRoleFilter(value as PeopleRoleFilter);
                setPage(0);
              }}
              options={ROLE_FILTERS.map((role) => ({
                value: role,
                label: intl.formatMessage({ id: role === "BORROWER" ? "lender.onboarding.applicationTypeBorrower" : "lender.onboarding.applicationTypeLender" }),
              }))}
            />
            <SelectField
              label={intl.formatMessage({ id: "lender.people.columnStatus" })}
              placeholder={intl.formatMessage({ id: "lender.people.filterAllStatuses" })}
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as PeopleStatusFilter);
                setPage(0);
              }}
              options={STATUS_FILTERS.map((status) => ({
                value: status,
                label: intl.formatMessage({ id: `lender.onboarding.status${status}` }),
              }))}
            />
            <div className="flex justify-end">
              <Button type="button" variant="ghost" density="compact" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                <FormattedMessage id="lender.members.clearFiltersButton" />
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {loadError && (
        <AlertBanner status="error" className="mb-4">
          {loadError}
        </AlertBanner>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        loading={isFetching}
        titleColumnId="fullName"
        metaColumnId="status"
        emptyState={
          <EmptyState>
            <FormattedMessage id={hasActiveFilters || query ? "lender.people.noMatchingMessage" : "lender.people.emptyStateMessage"} />
          </EmptyState>
        }
      />

      {totalElements > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <Text size="sm" variant="muted">
            <FormattedMessage id="lender.creditor.rowsPerPageLabel" values={{ count: PAGE_SIZE }} />
          </Text>
          <div className="flex items-center gap-3">
            <Text size="sm" variant="muted">
              <FormattedMessage id="lender.creditor.paginationRangeLabel" values={{ start: rangeStart, end: rangeEnd, total: totalElements }} />
            </Text>
            <button
              type="button"
              disabled={page <= 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              aria-label={intl.formatMessage({ id: "lender.shared.previousPageLabel" })}
              className="text-text-muted disabled:opacity-40"
            >
              ‹
            </button>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
              aria-label={intl.formatMessage({ id: "lender.shared.nextPageLabel" })}
              className="text-text-muted disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
